import FinalSalaryScheme from "../../assets/finalsalary/finalSalaryScheme";
import FinalSalaryType from "../../assets/finalsalary/finalSalaryType";
import Constants from "../../constants";
import { FinalSalary } from "../../finalSalary";
import { Retiree } from "../../retiree";
import { HighchartsIncomeChartSeries } from "./highchartsIncomeChartSeries";
import IncomeChartClass from "./incomeChartClass";
import { IncomeChartSeries } from "./incomeChartSeries";
import { IncomeSourceChartSeries } from "./incomeSourceChartSeries";
import { LegendItem, LegendStyle } from "./types";


export function getLegendItemsFromIncomeChartSeries(allIncomeChartSeries: Array<HighchartsIncomeChartSeries>, useSeriesLegendIndex: boolean): Array<LegendItem> {
    const items = new Array<LegendItem>();

    if (useSeriesLegendIndex) {
        for (const series of allIncomeChartSeries) {
            items.push(createLegendItem(series, series.legendIndex));
        }

        return items.sort((a, b) => a.index - b.index);
    }
    else
    {
        const classes = [...new Set(allIncomeChartSeries.map(s => s.class))];
        let index = 0;

        for (const cls of classes) {
            const seriesInThisClass = allIncomeChartSeries.filter(s => s.class === cls);

            const orderedByName = seriesInThisClass.sort((a, b) => b.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
            
            for (const seriesOfClass of orderedByName) {
                items.push(createLegendItem(seriesOfClass, index));
                index++;
            }
        }

        return items.reverse();
    }
}

export function mapIncomeSourceToIncomeSeries(retiree: Retiree, chartData: Array<IncomeSourceChartSeries>): Array<IncomeChartSeries> {

    if (retiree == null) {
        return;
    }

    const order = [
        'Final Salary Pension',
        'State Pension',
        'What to take from your defined contribution pension pot',
        'What to take from your savings pot',
        'Other income',
        'Investments',
        'Expected shortfall'
    ];

    const grouped: Record<string, IncomeChartSeries> = {};
    const orderLookup = {};
    chartData.forEach(item => {
        let sourceName = item.name;

        const matchedOrderKey = order.find(o =>
            sourceName.toLowerCase().startsWith(o.toLowerCase())
        );

        if (matchedOrderKey) {
            orderLookup[sourceName] = matchedOrderKey;
        }

        const legendIndex = order.indexOf(matchedOrderKey);

        if (!grouped[sourceName]) {
            const retirementYears = retiree.lifeExpectancy;
            grouped[sourceName] = {
                name: sourceName,
                data: Array(retirementYears).fill(null),
                class: item.class,
                type: 'column',
                legendIndex
            };
        }

        grouped[sourceName].data[item.year - 1] = item.value;
    });

    const incomeBySourceSeries: Array<IncomeChartSeries> = Object.values(grouped)
        .sort((a, b) => {
            const aKey = orderLookup[a.name] || a.name;
            const bKey = orderLookup[b.name] || b.name;
            const aIndex = order.findIndex(o => o.toLowerCase() === aKey.toLowerCase());
            const bIndex = order.findIndex(o => o.toLowerCase() === bKey.toLowerCase());
            return aIndex - bIndex;
        })
        .map(group => ({
            name: group.name,
            data: group.data,
            type: 'column',
            class: group.class,
            legendIndex: group.legendIndex
        }));

    const graphOrder = incomeBySourceSeries.reverse();

    applyCustomisedSeriesNames(graphOrder, retiree);

    return graphOrder;
}

export function createSeriesId(name: string): string {
    return name.replaceAll(" ", "-").toLowerCase();
}

function applyCustomisedSeriesNames(incomeSeries: Array<IncomeChartSeries>, retiree: Retiree): Array<IncomeChartSeries> {

    for (const incomeChartSeries of incomeSeries) {
        const asset = getFinalSalaryAsset(retiree, incomeChartSeries.name);

        if (!asset) {
            continue;
        }

        const schemeName = getFinalSalarySchemeNameForAsset(asset, retiree.finalSalaries.schemes);

        if (schemeName) {
            incomeChartSeries.name = schemeName;
        }
    }

    return incomeSeries;
}

function getFinalSalaryAsset(retiree: Retiree, assetName: string): null|FinalSalary {

    for (const finalSalaryAsset of retiree.finalSalaries.assets) {
        if (finalSalaryAsset.name != assetName) {
            continue;
        }

        return finalSalaryAsset;
    }

    return null;
}

function getFinalSalarySchemeNameForAsset(asset: FinalSalary, schemes: Array<FinalSalaryScheme>): string {
    
    for (const scheme of schemes) {
        if (scheme.assetIds.some(id => id === asset.id)) {
            return asset.pensionType === FinalSalaryType.Standard
                ? scheme.name
                : `${scheme.name} (BP)`;
        }
    }

    return "";
}

function createLegendItem(series: HighchartsIncomeChartSeries, index: number): LegendItem {
    const style: LegendStyle = series.class === IncomeChartClass.AggregateExpensesWithoutTaxAsExpense
    ? LegendStyle.Line
    : LegendStyle.Circle;

    return {
        key: series.id,
        name: series.name, 
        color: series.color?.toString() ?? Constants.Color.barChartDefault, 
        style,
        visible: true,
        index
    };
}
